/**
 * @param {string} formFieldName - the component name of the form input field
 * @param {Object} customConfig - custom config for form inputs
 * @param {Function} customConfigvalidatorFunction - custom validator function, wille be used if there is no validator handed down from props
 */

import { ref, computed } from 'vue'
import TooltipComponent from 'components/TooltipComponent/TooltipComponent.vue'
import formFieldFormBinder from 'foundation/js/formFieldFormBinder'

const autoSelectFirstRadio = function (props) {
    return props.radioOptions
        ? Object.values(props.radioOptions)[0].value
        : null
}

export default function (formFieldName, customConfig) {
    return {
        name: formFieldName,
        components: {
            TooltipComponent
        },
        props: {
            disabled: {
                type: String,
                required: false,
                default: ''
            },
            autocomplete: {
                type: String,
                default: 'off'
            },
            modelValue: {
                type: String,
                required: false
            },
            fieldName: {
                type: String,
                required: true
            },
            legend: {
                type: String,
                required: false
            },
            isRequired: {
                type: Boolean,
                required: false
            },
            label: {
                type: String,
                required: true
            },
            placeholder: {
                type: String,
                required: false
            },
            isValidationError: {
                // this props works in 2 ways
                // 1 - checkout page on webshop handles all field validation and will output result here
                // 2 - other forms like SFL forms will use this field to notify the field to show its possible errors
                type: Boolean,
                required: false,
                default: null
            },
            errorMessageLabel: {
                type: String,
                required: false
            },
            helpMessageLabel: {
                type: String,
                required: false
            },
            tooltipText: {
                type: String,
                required: false
            },
            type: {
                type: String,
                required: false,
                default: 'text'
            },
            minDate: {
                type: Date,
                required: false
            },
            maxlength: {
                type: Number,
                required: false
            },
            onChangeCallback: {
                type: Function,
                required: false,
                default: () => {}
            },
            formLink: {
                type: Function,
                required: false,
                default: null
            },
            validatorFunction: {
                type: Function,
                required: false,
                default: null
            },
            radioOptions: {
                type: Object,
                required: false
            },
            selectOptions: {
                type: Object,
                required: false
            },
            readOnly: {
                type: Boolean,
                required: false
            },
            isRichText: {
                type: Boolean,
                required: false
            }
        },
        setup(props) {
            const localValue = ref(
                props.modelValue ?? autoSelectFirstRadio(props) ?? ''
            )
            // generel validation functinality
            // Webshop handles field validation in the form logic, and sends results via props.isValidationErro
            // Other forms like SFL forms uses the props.isValidationError to signal the field to show any errors.
            const inputHasValidationErrors = computed(() =>
                // multiple ways of getting a validation of the input
                // 1 - external form validates the input = result is in props.isValidationError
                // 2 - recieved external validatorFunction via props = validate using that function
                // 3 - has internal function recived in commonConfig = validate use that function
                // If none of the above, just validation is cut short and passes with true
                props.isValidationError !== null
                    ? props.isValidationError
                    : props.validatorFunction
                      ? props.validatorFunction(localValue.value)
                      : typeof customConfig.validatorFunction === 'function'
                        ? customConfig.validatorFunction(localValue.value)
                        : true
            )
            const formLink = formFieldFormBinder(
                props.formLink,
                inputHasValidationErrors,
                localValue,
                props.onChangeCallback
            )

            return {
                formLink,
                inputHasValidationErrors,
                localValue
            }
        }
    }
}
